import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import "../src/style/style.scss";
import { BrowserRouter } from "react-router-dom";
import { UserContextProvider } from "./store/UserContext";
import { AuthContextProvider } from "./store/AuthContext";
import { PortalContextProvider } from "./store/PortalContext";
import { PoweredByContextProvider } from "./store/PoweredByContext";
import { LazyLoadingContextProvider } from "./store/LazyLoadingContext";
import ScrollOnMount from "./components/layout/ScrollOnMount";
import { DevSupport } from "@react-buddy/ide-toolbox";
import { ComponentPreviews, useInitial } from "./dev";
import { ParallaxProvider } from "react-scroll-parallax";

function PrerenderReady({ children, selector = "#root", timeout = 2000 }) {
    useEffect(() => {
        let settled = false;

        const setReady = () => {
            if (!settled) {
                // mark as ready for prerender tools to detect
                window.__PRERENDER_READY = true;
                settled = true;
                // keep console info minimal
                if (process.env.NODE_ENV !== "production") {
                    // eslint-disable-next-line no-console
                    console.debug("PRERENDER: window.__PRERENDER_READY = true");
                }
            }
        };

        // If app provides a custom async readiness function, use it
        try {
            if (typeof window.__PRERENDER_CUSTOM_READY === "function") {
                const maybePromise = window.__PRERENDER_CUSTOM_READY();
                if (maybePromise && typeof maybePromise.then === "function") {
                    maybePromise.then(setReady).catch(setReady);
                } else {
                    setReady();
                }
                return undefined;
            }
        } catch (e) {
            // ignore and continue to selector-based detection
        }

        const sel = typeof window.__PRERENDER_SELECTOR === "string" ? window.__PRERENDER_SELECTOR : selector;
        const el = document.querySelector(sel);

        // If element already has content, immediately mark ready
        if (el && el.innerHTML && el.innerHTML.trim().length > 0) {
            setReady();
            return undefined;
        }

        const observer = new MutationObserver(() => {
            const node = document.querySelector(sel);
            if (node && node.innerHTML && node.innerHTML.trim().length > 0) {
                observer.disconnect();
                setReady();
            }
        });

        // Observe the target element if present, otherwise observe body
        try {
            if (el) {
                observer.observe(el, { childList: true, subtree: true });
            } else {
                observer.observe(document.body, { childList: true, subtree: true });
            }
        } catch (e) {
            // fallback: if observation fails, set ready after timeout
        }

        const timer = setTimeout(() => {
            observer.disconnect();
            setReady();
        }, timeout);

        return () => {
            observer.disconnect();
            clearTimeout(timer);
        };
    }, [selector, timeout]);

    return children;
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
    <PrerenderReady>
        <LazyLoadingContextProvider>
            <PortalContextProvider>
                <PoweredByContextProvider>
                    <AuthContextProvider>
                        <UserContextProvider>
                            <ParallaxProvider>
                                <BrowserRouter>
                                    <ScrollOnMount>
                                        <DevSupport ComponentPreviews={ComponentPreviews} useInitialHook={useInitial}>
                                            <App />
                                        </DevSupport>
                                    </ScrollOnMount>
                                </BrowserRouter>
                            </ParallaxProvider>
                        </UserContextProvider>
                    </AuthContextProvider>
                </PoweredByContextProvider>
            </PortalContextProvider>
        </LazyLoadingContextProvider>
    </PrerenderReady>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
